import { z } from 'zod'

const menuPageBuilderButtonActionSchema = z.enum(['link', 'contactModal'])
export type MenuPageBuilderButtonAction = z.infer<typeof menuPageBuilderButtonActionSchema>

const menuPageBuilderContactSubjectSchema = z.enum(['rueckruf', 'termin', 'angebot', 'sonstiges'])
export type MenuPageBuilderContactSubject = z.infer<typeof menuPageBuilderContactSubjectSchema>

export const menuPageBuilderExternalRelOptions = ['auto', 'follow', 'nofollow', 'sponsored'] as const
const menuPageBuilderExternalRelSchema = z.enum(menuPageBuilderExternalRelOptions)
export type MenuPageBuilderExternalRel = z.infer<typeof menuPageBuilderExternalRelSchema>

export const menuPageBuilderButtonSchema = z.object({
  label: z.string(),
  href: z.string().optional(),
  action: menuPageBuilderButtonActionSchema.optional(),
  contactSubject: menuPageBuilderContactSubjectSchema.optional(),
  externalRel: menuPageBuilderExternalRelSchema.optional(),
})
export type MenuPageBuilderButton = z.infer<typeof menuPageBuilderButtonSchema>

export const menuPageBuilderBenefitItemSchema = z.object({
  title: z.string(),
  body: z.string().optional(),
  icon: z.string().optional(),
})
export type MenuPageBuilderBenefitItem = z.infer<typeof menuPageBuilderBenefitItemSchema>

export const menuPageBuilderLinkItemSchema = z.object({
  label: z.string(),
  href: z.string(),
  subtitle: z.string().optional(),
  imageSrc: z.string().optional(),
  imageAlt: z.string().optional(),
  externalRel: menuPageBuilderExternalRelSchema.optional(),
})
export type MenuPageBuilderLinkItem = z.infer<typeof menuPageBuilderLinkItemSchema>

export const menuPageBuilderFooterItemSchema = z.object({
  label: z.string(),
  href: z.string().optional(),
  icon: z.string().optional(),
  imageSrc: z.string().optional(),
  imageAlt: z.string().optional(),
  externalRel: menuPageBuilderExternalRelSchema.optional(),
})
export type MenuPageBuilderFooterItem = z.infer<typeof menuPageBuilderFooterItemSchema>

export const menuPageBuilderFooterColumnSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  bodyHtml: z.string().optional(),
  items: z.array(menuPageBuilderFooterItemSchema).optional(),
})
export type MenuPageBuilderFooterColumn = z.infer<typeof menuPageBuilderFooterColumnSchema>

const menuPageBuilderItemShapeSchema = z.enum(['square', 'rounded', 'round', 'oval'])
export type MenuPageBuilderItemShape = z.infer<typeof menuPageBuilderItemShapeSchema>

const menuPageBuilderItemSizeSchema = z.enum(['small', 'medium', 'large'])
export type MenuPageBuilderItemSize = z.infer<typeof menuPageBuilderItemSizeSchema>

const menuPageBuilderItemGapSchema = z.enum(['tight', 'normal', 'loose'])
export type MenuPageBuilderItemGap = z.infer<typeof menuPageBuilderItemGapSchema>

const menuPageBuilderItemShadowSchema = z.enum(['none', 'soft', 'medium'])
export type MenuPageBuilderItemShadow = z.infer<typeof menuPageBuilderItemShadowSchema>

const menuPageBuilderTextSizeSchema = z.enum(['small', 'medium', 'large'])
export type MenuPageBuilderTextSize = z.infer<typeof menuPageBuilderTextSizeSchema>

const menuPageBuilderLinkDisplaySchema = z.enum(['grid', 'slider', 'editorialSlider', 'compactSlider'])
export type MenuPageBuilderLinkDisplay = z.infer<typeof menuPageBuilderLinkDisplaySchema>

const menuPageBuilderNavigationDisplaySchema = z.enum(['none', 'separate', 'inline'])
export type MenuPageBuilderNavigationDisplay = z.infer<typeof menuPageBuilderNavigationDisplaySchema>

const menuPageBuilderProductsLayoutSchema = z.enum(['grid', 'highlight', 'compact', 'editorial', 'slider', 'sliderLine'])
export type MenuPageBuilderProductsLayout = z.infer<typeof menuPageBuilderProductsLayoutSchema>

const menuPageBuilderProductsDisplaySchema = z.enum(['grid', 'slider', 'sliderLine'])
export type MenuPageBuilderProductsDisplay = z.infer<typeof menuPageBuilderProductsDisplaySchema>

export const menuPageBuilderSearchSchema = z.object({
  showCategories: z.boolean().optional(),
  showBrands: z.boolean().optional(),
  showProducts: z.boolean().optional(),
  items: z.array(menuPageBuilderLinkItemSchema).optional(),
})
export type MenuPageBuilderSearch = z.infer<typeof menuPageBuilderSearchSchema>

export const menuPageBuilderProductsSettingsSchema = z.object({
  columnsPerRow: z.number().int().min(1).max(6).optional(),
  columnsPerRowMobile: z.number().int().min(1).max(3).optional(),
})
export type MenuPageBuilderProductsSettings = z.infer<typeof menuPageBuilderProductsSettingsSchema>

export const menuPageBuilderLayoutSchema = z.object({
  stylePreset: z.enum(['individual', 'westwing']).optional(),
  navigationPosition: z.enum(['side', 'top']).optional(),
  navigationDisplay: menuPageBuilderNavigationDisplaySchema.optional(),
  heroMobileMode: z.enum(['content', 'fullscreen']).optional(),
  contentWidth: z.enum(['contained', 'wide', 'full']).optional(),
  magazinePlacement: z.enum(['afterHero', 'afterFilters', 'beforeProducts', 'afterProducts', 'hidden']).optional(),
  hideLegacyHeader: z.boolean().optional(),
})
export type MenuPageBuilderLayout = z.infer<typeof menuPageBuilderLayoutSchema>

export const menuPageBuilderThemeSchema = z.object({
  fontFamily: z.string().optional(),
  backgroundColor: z.string().optional(),
  surfaceColor: z.string().optional(),
  textColor: z.string().optional(),
  mutedTextColor: z.string().optional(),
  accentColor: z.string().optional(),
  accentContrastColor: z.string().optional(),
  buttonBackgroundColor: z.string().optional(),
  buttonHoverColor: z.string().optional(),
  buttonTextColor: z.string().optional(),
  purchaseButtonBackgroundColor: z.string().optional(),
  purchaseButtonHoverColor: z.string().optional(),
  purchaseButtonTextColor: z.string().optional(),
  infoColor: z.string().optional(),
  successColor: z.string().optional(),
  warningColor: z.string().optional(),
  errorColor: z.string().optional(),
  navigationBackgroundColor: z.string().optional(),
  navigationTextColor: z.string().optional(),
  navigationOverlayOpacity: z.number().min(0).max(100).optional(),
})
export type MenuPageBuilderTheme = z.infer<typeof menuPageBuilderThemeSchema>

export const menuPageBuilderFooterSchema = z.object({
  enabled: z.boolean().optional(),
  showTopBrands: z.boolean().optional(),
  showInfosLinks: z.boolean().optional(),
  showFootnotes: z.boolean().optional(),
  showTimestamp: z.boolean().optional(),
  showDivider: z.boolean().optional(),
  dividerMarginTop: z.number().int().min(0).max(240).optional(),
  infosPaddingTop: z.number().int().min(0).max(240).optional(),
  infosPaddingBottom: z.number().int().min(0).max(240).optional(),
  legalPaddingTop: z.number().int().min(0).max(240).optional(),
  legalPaddingBottom: z.number().int().min(0).max(240).optional(),
  footnotesPaddingTop: z.number().int().min(0).max(240).optional(),
  footnotesPaddingBottom: z.number().int().min(0).max(240).optional(),
  topBrandsTitle: z.string().optional(),
  copyrightText: z.string().optional(),
  backgroundColor: z.string().optional(),
  surfaceColor: z.string().optional(),
  textColor: z.string().optional(),
  mutedTextColor: z.string().optional(),
  accentColor: z.string().optional(),
  brandSectionBackgroundColor: z.string().optional(),
  legalBackgroundColor: z.string().optional(),
  columnsPerRow: z.number().int().min(1).max(6).optional(),
  columns: z.array(menuPageBuilderFooterColumnSchema).optional(),
  topBrandsBlock: z.lazy(() => menuPageBuilderBrandListBlockSchema).optional(),
  footnotesPrimaryHtml: z.string().optional(),
  footnotesSecondaryHtml: z.string().optional(),
})
export type MenuPageBuilderFooter = z.infer<typeof menuPageBuilderFooterSchema>

export const menuPageBuilderFooterSpacingDefaults = {
  showDivider: false,
  dividerMarginTop: 32,
  infosPaddingTop: 40,
  infosPaddingBottom: 40,
  legalPaddingTop: 10,
  legalPaddingBottom: 10,
  footnotesPaddingTop: 18,
  footnotesPaddingBottom: 28,
} as const

export const menuPageBuilderHeroSchema = z.object({
  layout: z.enum(['background', 'split']).optional(),
  eyebrow: z.string().optional(),
  title: z.string().optional(),
  body: z.string().optional(),
  imageSrc: z.string().optional(),
  imageAlt: z.string().optional(),
  mobileImageSrc: z.string().optional(),
  mobileImageAlt: z.string().optional(),
  imageFit: z.enum(['cover', 'contain']).optional(),
  cta: menuPageBuilderButtonSchema.optional(),
  secondaryCta: menuPageBuilderButtonSchema.optional(),
  align: z.enum(['left', 'center']).optional(),
  height: z.enum(['medium', 'large', 'screen']).optional(),
  overlayColor: z.string().optional(),
  overlayOpacity: z.number().min(0).max(100).optional(),
})
export type MenuPageBuilderHero = z.infer<typeof menuPageBuilderHeroSchema>

const blockBaseSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  backgroundColor: z.string().optional(),
  paddingTop: z.number().int().min(0).max(240).optional(),
  paddingBottom: z.number().int().min(0).max(240).optional(),
  divider: z.enum(['none', 'top', 'bottom', 'both']).optional(),
  actionLabel: z.string().optional(),
  actionHref: z.string().optional(),
})

export const menuPageBuilderBannerItemSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  body: z.string().optional(),
  imageTitle: z.string().optional(),
  imageSubtitle: z.string().optional(),
  imageBody: z.string().optional(),
  imageSrc: z.string().optional(),
  imageAlt: z.string().optional(),
  mobileImageSrc: z.string().optional(),
  mobileImageAlt: z.string().optional(),
  cta: menuPageBuilderButtonSchema.optional(),
  secondaryCta: menuPageBuilderButtonSchema.optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  overlayColor: z.string().optional(),
  overlayOpacity: z.number().min(0).max(100).optional(),
})
export type MenuPageBuilderBannerItem = z.infer<typeof menuPageBuilderBannerItemSchema>

export const menuPageBuilderBannerBlockSchema = blockBaseSchema.extend({
  type: z.literal('banner'),
  variant: z.enum(['topStrip', 'stage', 'split', 'story']).optional(),
  mobileLayout: z.enum(['default', 'compactText']).optional(),
  mobileTextSize: z.enum(['normal', 'small']).optional(),
  mobileTextShadow: z.boolean().optional(),
  body: z.string().optional(),
  imageTitle: z.string().optional(),
  imageSubtitle: z.string().optional(),
  imageBody: z.string().optional(),
  titleHref: z.string().optional(),
  subtitleHref: z.string().optional(),
  bodyHref: z.string().optional(),
  imageSrc: z.string(),
  imageAlt: z.string().optional(),
  mobileImageSrc: z.string().optional(),
  mobileImageAlt: z.string().optional(),
  cta: menuPageBuilderButtonSchema.optional(),
  secondaryCta: menuPageBuilderButtonSchema.optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  minHeight: z.enum(['small', 'medium', 'large']).optional(),
  overlayColor: z.string().optional(),
  overlayOpacity: z.number().min(0).max(100).optional(),
  items: z.array(menuPageBuilderBannerItemSchema).optional(),
})
export type MenuPageBuilderBannerBlock = z.infer<typeof menuPageBuilderBannerBlockSchema>

export const menuPageBuilderRichTextBlockSchema = blockBaseSchema.extend({
  type: z.literal('richText'),
  bodyHtml: z.string(),
  align: z.enum(['left', 'center']).optional(),
  maxWidth: z.enum(['narrow', 'default', 'wide']).optional(),
  readMoreEnabled: z.boolean().optional(),
  readMoreLines: z.number().int().min(1).max(40).optional(),
  readMoreLabel: z.string().optional(),
  readLessLabel: z.string().optional(),
})
export type MenuPageBuilderRichTextBlock = z.infer<typeof menuPageBuilderRichTextBlockSchema>

export const menuPageBuilderBenefitsBlockSchema = blockBaseSchema.extend({
  type: z.literal('benefits'),
  items: z.array(menuPageBuilderBenefitItemSchema),
  columns: z.number().int().min(1).max(6).optional(),
  variant: z.enum(['grid', 'strip', 'trustBar', 'reviewGrid']).optional(),
  reviewSource: z.enum(['manual', 'google']).optional(),
  googleRating: z.string().optional(),
  googleReviewCount: z.string().optional(),
  googleReviewHref: z.string().optional(),
  googlePlaceId: z.string().optional(),
  googlePlaceQuery: z.string().optional(),
})
export type MenuPageBuilderBenefitsBlock = z.infer<typeof menuPageBuilderBenefitsBlockSchema>

export const menuPageBuilderCategoryListBlockSchema = blockBaseSchema.extend({
  type: z.literal('categoryList'),
  items: z.array(menuPageBuilderLinkItemSchema),
  columns: z.number().int().min(1).max(8).optional(),
  display: menuPageBuilderLinkDisplaySchema.optional(),
  itemShape: menuPageBuilderItemShapeSchema.optional(),
  itemSize: menuPageBuilderItemSizeSchema.optional(),
  textSize: menuPageBuilderTextSizeSchema.optional(),
  itemGap: menuPageBuilderItemGapSchema.optional(),
  itemShadow: menuPageBuilderItemShadowSchema.optional(),
  stretchItems: z.boolean().optional(),
})
export type MenuPageBuilderCategoryListBlock = z.infer<typeof menuPageBuilderCategoryListBlockSchema>

export const menuPageBuilderCategorySliderBlockSchema = blockBaseSchema.extend({
  type: z.literal('categorySlider'),
  items: z.array(menuPageBuilderLinkItemSchema),
  variant: z.enum(['editorialCircle', 'compactStrip']).optional(),
  itemShape: menuPageBuilderItemShapeSchema.optional(),
  itemSize: menuPageBuilderItemSizeSchema.optional(),
  textSize: menuPageBuilderTextSizeSchema.optional(),
  itemGap: menuPageBuilderItemGapSchema.optional(),
  itemShadow: menuPageBuilderItemShadowSchema.optional(),
  stretchItems: z.boolean().optional(),
})
export type MenuPageBuilderCategorySliderBlock = z.infer<typeof menuPageBuilderCategorySliderBlockSchema>

export const menuPageBuilderBrandListBlockSchema = blockBaseSchema.extend({
  type: z.literal('brandList'),
  brandIds: z.array(z.number().int().positive()),
  items: z.array(menuPageBuilderLinkItemSchema).optional(),
  columns: z.number().int().min(1).max(6).optional(),
  display: menuPageBuilderLinkDisplaySchema.optional(),
  itemShape: menuPageBuilderItemShapeSchema.optional(),
  itemSize: menuPageBuilderItemSizeSchema.optional(),
  itemGap: menuPageBuilderItemGapSchema.optional(),
  itemShadow: menuPageBuilderItemShadowSchema.optional(),
  logoStyle: z.enum(['mono', 'color']).optional(),
  logoOffsetX: z.number().min(-80).max(80).optional(),
  logoOffsetY: z.number().min(-80).max(80).optional(),
  showLabel: z.boolean().optional(),
})
export type MenuPageBuilderBrandListBlock = z.infer<typeof menuPageBuilderBrandListBlockSchema>

export const menuPageBuilderProductsBlockSchema = blockBaseSchema.extend({
  type: z.literal('products'),
  productKeys: z.array(z.string()),
  selectionMode: z.enum(['page', 'manual']).optional(),
  layout: menuPageBuilderProductsLayoutSchema.optional(),
  display: menuPageBuilderProductsDisplaySchema.optional(),
  cardBackgroundColor: z.string().optional(),
  imageSizePx: z.number().int().min(80).max(420).optional(),
  showAttributes: z.boolean().optional(),
  showHookText: z.boolean().optional(),
  showPrice: z.boolean().optional(),
  showSpecialPrice: z.boolean().optional(),
  showAvailability: z.boolean().optional(),
  showHoverEffect: z.boolean().optional(),
  preferMoodImages: z.boolean().optional(),
  showWishlist: z.boolean().optional(),
})
export type MenuPageBuilderProductsBlock = z.infer<typeof menuPageBuilderProductsBlockSchema>

export const getMenuPageBuilderProductSelectionMode = (
  block: Pick<MenuPageBuilderProductsBlock, 'productKeys' | 'selectionMode'>
): 'page' | 'manual' => block.selectionMode || (block.productKeys.length > 0 ? 'manual' : 'page')

export const menuPageBuilderBlockSchema = z.discriminatedUnion('type', [
  menuPageBuilderBannerBlockSchema,
  menuPageBuilderRichTextBlockSchema,
  menuPageBuilderCategoryListBlockSchema,
  menuPageBuilderCategorySliderBlockSchema,
  menuPageBuilderBrandListBlockSchema,
  menuPageBuilderProductsBlockSchema,
  menuPageBuilderBenefitsBlockSchema,
])
export type MenuPageBuilderBlock = z.infer<typeof menuPageBuilderBlockSchema>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const sanitizeString = (value: unknown) => {
  if (typeof value !== 'string') return undefined
  const sanitized = value.trim()
  return sanitized || undefined
}

const sanitizeExternalRel = (value: unknown): MenuPageBuilderExternalRel | undefined =>
  typeof value === 'string' && menuPageBuilderExternalRelOptions.includes(value as MenuPageBuilderExternalRel)
    ? value as MenuPageBuilderExternalRel
    : undefined

const sanitizeFooterItems = (value: unknown): MenuPageBuilderFooterItem[] =>
  Array.isArray(value)
    ? value.reduce<MenuPageBuilderFooterItem[]>((acc, entry) => {
        if (!isRecord(entry)) return acc

        const label = sanitizeString(entry.label)
        const imageSrc = sanitizeString(entry.imageSrc)
        const icon = sanitizeString(entry.icon)
        if (!label && !imageSrc && !icon) return acc

        acc.push({
          label: label || '',
          href: sanitizeString(entry.href),
          icon,
          imageSrc,
          imageAlt: sanitizeString(entry.imageAlt),
          externalRel: sanitizeExternalRel(entry.externalRel),
        })

        return acc
      }, [])
    : []

const sanitizeFooterColumns = (value: unknown): MenuPageBuilderFooterColumn[] =>
  Array.isArray(value)
    ? value.reduce<MenuPageBuilderFooterColumn[]>((acc, entry) => {
        if (!isRecord(entry)) return acc

        const title = sanitizeString(entry.title)
        const bodyHtml = sanitizeString(entry.bodyHtml)
        const items = sanitizeFooterItems(entry.items)

        if (!title && !bodyHtml && !items.length) return acc

        acc.push({
          id: sanitizeString(entry.id),
          title: title || '',
          bodyHtml,
          items: items.length ? items : undefined,
        })

        return acc
      }, [])
    : []

const sanitizeBoolean = (value: unknown) =>
  typeof value === 'boolean' ? value : undefined

const sanitizeNumber = (value: unknown, min: number, max: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return Math.min(max, Math.max(min, value))
}

const sanitizeInt = (value: unknown, min: number, max: number) => {
  if (typeof value !== 'number' || !Number.isInteger(value)) return undefined
  return Math.min(max, Math.max(min, value))
}

const sanitizePositiveIntArray = (value: unknown) =>
  Array.isArray(value)
    ? value
        .map(entry => typeof entry === 'number' && Number.isInteger(entry) && entry > 0 ? entry : undefined)
        .filter((entry): entry is number => entry !== undefined)
    : []

const sanitizeEnum = <T extends readonly string[]>(
  value: unknown,
  allowed: T
): T[number] | undefined =>
  typeof value === 'string' && allowed.includes(value)
    ? value as T[number]
    : undefined

const hasDefinedValue = (value: Record<string, unknown>) =>
  Object.values(value).some(entry => entry !== undefined)

const hasHeroContent = (value?: Partial<MenuPageBuilderHero>) =>
  Boolean(
    value?.eyebrow
    || value?.title
    || value?.body
    || value?.imageSrc
    || value?.mobileImageSrc
    || value?.cta
    || value?.secondaryCta
  )

const stripUndefinedDeep = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value
      .map(entry => stripUndefinedDeep(entry))
      .filter(entry => entry !== undefined) as unknown as T
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce((acc, [key, entry]) => {
      if (entry === undefined) return acc
      acc[key] = stripUndefinedDeep(entry)
      return acc
    }, {} as Record<string, unknown>) as T
  }

  return value
}

export const sanitizeMenuPageBuilderButton = (
  value: unknown
): MenuPageBuilderButton | undefined => {
  if (!isRecord(value)) return undefined

  const label = sanitizeString(value.label)
  const href = sanitizeString(value.href)
  const legacyModalHref = href === '#contact-modal' || href === '#kontaktmodal'
  const action =
    sanitizeEnum(value.action, ['link', 'contactModal'] as const)
    || (legacyModalHref ? 'contactModal' : undefined)
  const contactSubject = sanitizeEnum(value.contactSubject, ['rueckruf', 'termin', 'angebot', 'sonstiges'] as const)

  if (!label) return undefined
  if (action !== 'contactModal' && !href) return undefined

  return {
    label,
    href: href || '',
    action: action === 'contactModal' ? action : undefined,
    contactSubject,
    externalRel: sanitizeExternalRel(value.externalRel),
  }
}

export const sanitizeMenuPageBuilderLinkItem = (
  value: unknown
): MenuPageBuilderLinkItem | undefined => {
  if (!isRecord(value)) return undefined

  const label = sanitizeString(value.label)
  const href = sanitizeString(value.href)

  if (!label || !href) return undefined

  return {
    label,
    href,
    subtitle: sanitizeString(value.subtitle),
    imageSrc: sanitizeString(value.imageSrc),
    imageAlt: sanitizeString(value.imageAlt),
    externalRel: sanitizeExternalRel(value.externalRel),
  }
}

const sanitizeBlockBase = (value: Record<string, unknown>) => ({
  id: sanitizeString(value.id),
  title: sanitizeString(value.title),
  subtitle: sanitizeString(value.subtitle),
  backgroundColor: sanitizeString(value.backgroundColor),
  paddingTop: sanitizeInt(value.paddingTop, 0, 240),
  paddingBottom: sanitizeInt(value.paddingBottom, 0, 240),
  divider: sanitizeEnum(value.divider, ['none', 'top', 'bottom', 'both'] as const),
  actionLabel: sanitizeString(value.actionLabel),
  actionHref: sanitizeString(value.actionHref),
})

const sanitizeBannerItem = (
  value: unknown
): MenuPageBuilderBannerItem | undefined => {
  if (!isRecord(value)) return undefined

  const title = sanitizeString(value.title)
  const subtitle = sanitizeString(value.subtitle)
  const body = sanitizeString(value.body)
  const imageTitle = sanitizeString(value.imageTitle)
  const imageSubtitle = sanitizeString(value.imageSubtitle)
  const imageBody = sanitizeString(value.imageBody)
  const imageSrc = sanitizeString(value.imageSrc)
  const cta = sanitizeMenuPageBuilderButton(value.cta)
  const secondaryCta = sanitizeMenuPageBuilderButton(value.secondaryCta)

  if (!title && !subtitle && !body && !imageTitle && !imageSubtitle && !imageBody && !imageSrc && !cta && !secondaryCta) return undefined

  return {
    id: sanitizeString(value.id),
    title,
    subtitle,
    body,
    imageTitle,
    imageSubtitle,
    imageBody,
    imageSrc,
    imageAlt: sanitizeString(value.imageAlt),
    mobileImageSrc: sanitizeString(value.mobileImageSrc),
    mobileImageAlt: sanitizeString(value.mobileImageAlt),
    cta,
    secondaryCta,
    align: sanitizeEnum(value.align, ['left', 'center', 'right'] as const),
    overlayColor: sanitizeString(value.overlayColor),
    overlayOpacity: sanitizeNumber(value.overlayOpacity, 0, 100),
  }
}

const sanitizeBannerBlock = (
  value: Record<string, unknown>
): MenuPageBuilderBannerBlock | undefined => {
  const imageSrc = sanitizeString(value.imageSrc) || ''
  const base = sanitizeBlockBase(value)
  const body = sanitizeString(value.body)
  const imageTitle = sanitizeString(value.imageTitle)
  const imageSubtitle = sanitizeString(value.imageSubtitle)
  const imageBody = sanitizeString(value.imageBody)
  const cta = sanitizeMenuPageBuilderButton(value.cta)
  const secondaryCta = sanitizeMenuPageBuilderButton(value.secondaryCta)
  const items = Array.isArray(value.items)
    ? value.items
        .map(item => sanitizeBannerItem(item))
        .filter((item): item is MenuPageBuilderBannerItem => !!item)
    : []

  if (!imageSrc && !base.title && !base.subtitle && !body && !imageTitle && !imageSubtitle && !imageBody && !cta && !secondaryCta && !items.length) {
    return undefined
  }

  return {
    type: 'banner',
    ...base,
    variant: sanitizeEnum(value.variant, ['topStrip', 'stage', 'split', 'story'] as const),
    mobileLayout: sanitizeEnum(value.mobileLayout, ['default', 'compactText'] as const),
    mobileTextSize: sanitizeEnum(value.mobileTextSize, ['normal', 'small'] as const),
    mobileTextShadow: value.mobileTextShadow === true || value.mobileTextShadow === 'true',
    body,
    imageTitle,
    imageSubtitle,
    imageBody,
    titleHref: sanitizeString(value.titleHref),
    subtitleHref: sanitizeString(value.subtitleHref),
    bodyHref: sanitizeString(value.bodyHref),
    imageSrc,
    imageAlt: sanitizeString(value.imageAlt),
    mobileImageSrc: sanitizeString(value.mobileImageSrc),
    mobileImageAlt: sanitizeString(value.mobileImageAlt),
    cta,
    secondaryCta,
    align: sanitizeEnum(value.align, ['left', 'center', 'right'] as const),
    minHeight: sanitizeEnum(value.minHeight, ['small', 'medium', 'large'] as const),
    overlayColor: sanitizeString(value.overlayColor),
    overlayOpacity: sanitizeNumber(value.overlayOpacity, 0, 100),
    items: items.length ? items : undefined,
  }
}

const sanitizeLinkBlock = (
  value: Record<string, unknown>,
  type: 'categoryList' | 'categorySlider'
):
  | MenuPageBuilderCategoryListBlock
  | MenuPageBuilderCategorySliderBlock
  | undefined => {
  const items = Array.isArray(value.items)
    ? value.items
        .map(item => sanitizeMenuPageBuilderLinkItem(item))
        .filter((item): item is MenuPageBuilderLinkItem => !!item)
    : []

  const base = sanitizeBlockBase(value)

  if (!items.length && !base.title && !base.subtitle) return undefined

  const sanitized = {
    type,
    ...base,
    items,
  }

  if (type === 'categorySlider') {
    return {
      ...sanitized,
      variant: sanitizeEnum(value.variant, ['editorialCircle', 'compactStrip'] as const),
      itemShape: sanitizeEnum(value.itemShape, ['square', 'rounded', 'round', 'oval'] as const),
      itemSize: sanitizeEnum(value.itemSize, ['small', 'medium', 'large'] as const),
      textSize: sanitizeEnum(value.textSize, ['small', 'medium', 'large'] as const),
      itemGap: sanitizeEnum(value.itemGap, ['tight', 'normal', 'loose'] as const),
      itemShadow: sanitizeEnum(value.itemShadow, ['none', 'soft', 'medium'] as const),
      stretchItems: sanitizeBoolean(value.stretchItems),
    }
  }

  return {
    ...sanitized,
    columns: sanitizeInt(value.columns, 1, 8),
    display: sanitizeEnum(value.display, ['grid', 'slider', 'editorialSlider', 'compactSlider'] as const),
    itemShape: sanitizeEnum(value.itemShape, ['square', 'rounded', 'round', 'oval'] as const),
    itemSize: sanitizeEnum(value.itemSize, ['small', 'medium', 'large'] as const),
    textSize: sanitizeEnum(value.textSize, ['small', 'medium', 'large'] as const),
    itemGap: sanitizeEnum(value.itemGap, ['tight', 'normal', 'loose'] as const),
    itemShadow: sanitizeEnum(value.itemShadow, ['none', 'soft', 'medium'] as const),
    stretchItems: sanitizeBoolean(value.stretchItems),
  }
}

const sanitizeBrandListBlock = (
  value: Record<string, unknown>
): MenuPageBuilderBrandListBlock | undefined => {
  const base = sanitizeBlockBase(value)
  const brandIds = sanitizePositiveIntArray(value.brandIds)
  const legacyItems = Array.isArray(value.items)
    ? value.items
        .map(item => sanitizeMenuPageBuilderLinkItem(item))
        .filter((item): item is MenuPageBuilderLinkItem => !!item)
    : []

  if (!brandIds.length && !legacyItems.length && !base.title && !base.subtitle) return undefined

  return {
    type: 'brandList',
    ...base,
    brandIds,
    items: legacyItems.length ? legacyItems : undefined,
    columns: sanitizeInt(value.columns, 1, 6),
    display: sanitizeEnum(value.display, ['grid', 'slider', 'editorialSlider', 'compactSlider'] as const),
    itemShape: sanitizeEnum(value.itemShape, ['square', 'rounded', 'round', 'oval'] as const),
    itemSize: sanitizeEnum(value.itemSize, ['small', 'medium', 'large'] as const),
    itemGap: sanitizeEnum(value.itemGap, ['tight', 'normal', 'loose'] as const),
    itemShadow: sanitizeEnum(value.itemShadow, ['none', 'soft', 'medium'] as const),
    logoStyle: sanitizeEnum(value.logoStyle, ['mono', 'color'] as const),
    logoOffsetX: sanitizeNumber(value.logoOffsetX, -80, 80),
    logoOffsetY: sanitizeNumber(value.logoOffsetY, -80, 80),
    showLabel: sanitizeBoolean(value.showLabel),
  }
}

const sanitizeRichTextBlock = (
  value: Record<string, unknown>
): MenuPageBuilderRichTextBlock | undefined => {
  const bodyHtml = sanitizeString(value.bodyHtml) || ''
  const base = sanitizeBlockBase(value)

  if (!bodyHtml && !base.title && !base.subtitle) return undefined

  return {
    type: 'richText',
    ...base,
    bodyHtml,
    align: sanitizeEnum(value.align, ['left', 'center'] as const),
    maxWidth: sanitizeEnum(value.maxWidth, ['narrow', 'default', 'wide'] as const),
    readMoreEnabled: sanitizeBoolean(value.readMoreEnabled),
    readMoreLines: sanitizeInt(value.readMoreLines, 1, 40),
    readMoreLabel: sanitizeString(value.readMoreLabel),
    readLessLabel: sanitizeString(value.readLessLabel),
  }
}

const sanitizeBenefitItems = (value: unknown): MenuPageBuilderBenefitItem[] =>
  Array.isArray(value)
    ? value.reduce<MenuPageBuilderBenefitItem[]>((acc, entry) => {
        if (!isRecord(entry)) return acc

        const title = sanitizeString(entry.title)
        const body = sanitizeString(entry.body)
        const icon = sanitizeString(entry.icon)

        if (!title && !body && !icon) return acc

        acc.push({
          title: title || '',
          body,
          icon,
        })

        return acc
      }, [])
    : []

const sanitizeBenefitsBlock = (
  value: Record<string, unknown>
): MenuPageBuilderBenefitsBlock | undefined => {
  const base = sanitizeBlockBase(value)
  const items = sanitizeBenefitItems(value.items)

  if (!items.length && !base.title && !base.subtitle) return undefined

  return {
    type: 'benefits',
    ...base,
    items,
    columns: sanitizeInt(value.columns, 1, 6),
    variant: sanitizeEnum(value.variant, ['grid', 'strip', 'trustBar', 'reviewGrid'] as const),
    reviewSource: sanitizeEnum(value.reviewSource, ['manual', 'google'] as const),
    googleRating: sanitizeString(value.googleRating),
    googleReviewCount: sanitizeString(value.googleReviewCount),
    googleReviewHref: sanitizeString(value.googleReviewHref),
    googlePlaceId: sanitizeString(value.googlePlaceId),
    googlePlaceQuery: sanitizeString(value.googlePlaceQuery),
  }
}

const sanitizeProductsBlock = (
  value: Record<string, unknown>
): MenuPageBuilderProductsBlock | undefined => {
  const productKeys = Array.isArray(value.productKeys)
    ? value.productKeys
        .map(key => sanitizeString(key))
        .filter((key): key is string => !!key)
    : []

  const base = sanitizeBlockBase(value)

  return {
    type: 'products',
    ...base,
    productKeys,
    selectionMode: sanitizeEnum(value.selectionMode, ['page', 'manual'] as const),
    layout: sanitizeEnum(value.layout, ['grid', 'highlight', 'compact', 'editorial', 'slider', 'sliderLine'] as const),
    display: sanitizeEnum(value.display, ['grid', 'slider', 'sliderLine'] as const),
    cardBackgroundColor: sanitizeString(value.cardBackgroundColor),
    imageSizePx: sanitizeInt(value.imageSizePx, 80, 420),
    showAttributes: sanitizeBoolean(value.showAttributes),
    showHookText: sanitizeBoolean(value.showHookText),
    showPrice: sanitizeBoolean(value.showPrice),
    showSpecialPrice: sanitizeBoolean(value.showSpecialPrice),
    showAvailability: sanitizeBoolean(value.showAvailability),
    showHoverEffect: sanitizeBoolean(value.showHoverEffect),
    preferMoodImages: sanitizeBoolean(value.preferMoodImages),
    showWishlist: sanitizeBoolean(value.showWishlist),
  }
}

export const sanitizeMenuPageBuilderBlock = (
  value: unknown
): MenuPageBuilderBlock | undefined => {
  if (!isRecord(value)) return undefined

  switch (value.type) {
    case 'banner':
      return sanitizeBannerBlock(value)
    case 'categoryList':
      return sanitizeLinkBlock(value, 'categoryList')
    case 'categorySlider':
      return sanitizeLinkBlock(value, 'categorySlider')
    case 'brandList':
      return sanitizeBrandListBlock(value)
    case 'richText':
      return sanitizeRichTextBlock(value)
    case 'benefits':
      return sanitizeBenefitsBlock(value)
    case 'products':
      return sanitizeProductsBlock(value)
    default:
      return undefined
  }
}

export const sanitizeMenuPageBuilder = (
  value: unknown
): MenuPageBuilder | undefined => {
  if (!isRecord(value)) return undefined

  const layout = isRecord(value.layout)
    ? {
        stylePreset: sanitizeEnum(value.layout.stylePreset, ['individual', 'westwing'] as const),
        navigationPosition: sanitizeEnum(value.layout.navigationPosition, ['side', 'top'] as const),
        navigationDisplay: sanitizeEnum(value.layout.navigationDisplay, ['none', 'separate', 'inline'] as const),
        heroMobileMode: sanitizeEnum(value.layout.heroMobileMode, ['content', 'fullscreen'] as const),
        contentWidth: sanitizeEnum(value.layout.contentWidth, ['contained', 'wide', 'full'] as const),
        magazinePlacement: sanitizeEnum(value.layout.magazinePlacement, ['afterHero', 'afterFilters', 'beforeProducts', 'afterProducts', 'hidden'] as const),
        hideLegacyHeader: sanitizeBoolean(value.layout.hideLegacyHeader),
      }
    : undefined

  const theme = isRecord(value.theme)
    ? {
        fontFamily: sanitizeString(value.theme.fontFamily),
        backgroundColor: sanitizeString(value.theme.backgroundColor),
        surfaceColor: sanitizeString(value.theme.surfaceColor),
        textColor: sanitizeString(value.theme.textColor),
        mutedTextColor: sanitizeString(value.theme.mutedTextColor),
        accentColor: sanitizeString(value.theme.accentColor),
        accentContrastColor: sanitizeString(value.theme.accentContrastColor),
        buttonBackgroundColor: sanitizeString(value.theme.buttonBackgroundColor),
        buttonHoverColor: sanitizeString(value.theme.buttonHoverColor),
        buttonTextColor: sanitizeString(value.theme.buttonTextColor),
        purchaseButtonBackgroundColor: sanitizeString(value.theme.purchaseButtonBackgroundColor),
        purchaseButtonHoverColor: sanitizeString(value.theme.purchaseButtonHoverColor),
        purchaseButtonTextColor: sanitizeString(value.theme.purchaseButtonTextColor),
        infoColor: sanitizeString(value.theme.infoColor),
        successColor: sanitizeString(value.theme.successColor),
        warningColor: sanitizeString(value.theme.warningColor),
        errorColor: sanitizeString(value.theme.errorColor),
        navigationBackgroundColor: sanitizeString(value.theme.navigationBackgroundColor),
        navigationTextColor: sanitizeString(value.theme.navigationTextColor),
        navigationOverlayOpacity: sanitizeNumber(value.theme.navigationOverlayOpacity, 0, 100),
      }
    : undefined

  const hero = isRecord(value.hero)
    ? (() => {
        const hero = {
          eyebrow: sanitizeString(value.hero.eyebrow),
          layout: sanitizeEnum(value.hero.layout, ['background', 'split'] as const),
          title: sanitizeString(value.hero.title),
          body: sanitizeString(value.hero.body),
          imageSrc: sanitizeString(value.hero.imageSrc),
          imageAlt: sanitizeString(value.hero.imageAlt),
          mobileImageSrc: sanitizeString(value.hero.mobileImageSrc),
          mobileImageAlt: sanitizeString(value.hero.mobileImageAlt),
          imageFit: sanitizeEnum(value.hero.imageFit, ['cover', 'contain'] as const),
          cta: sanitizeMenuPageBuilderButton(value.hero.cta),
          secondaryCta: sanitizeMenuPageBuilderButton(value.hero.secondaryCta),
          align: sanitizeEnum(value.hero.align, ['left', 'center'] as const),
          height: sanitizeEnum(value.hero.height, ['medium', 'large', 'screen'] as const),
          overlayColor: sanitizeString(value.hero.overlayColor),
          overlayOpacity: sanitizeNumber(value.hero.overlayOpacity, 0, 100),
        }

        return hasHeroContent(hero) ? hero : undefined
      })()
    : undefined

  const blocks = Array.isArray(value.blocks)
    ? value.blocks
        .map(block => sanitizeMenuPageBuilderBlock(block))
        .filter((block): block is MenuPageBuilderBlock => !!block)
    : undefined

  const search = isRecord(value.search)
    ? {
        showCategories: sanitizeBoolean(value.search.showCategories),
        showBrands: sanitizeBoolean(value.search.showBrands),
        showProducts: sanitizeBoolean(value.search.showProducts),
        items: Array.isArray(value.search.items)
          ? value.search.items
              .map(item => sanitizeMenuPageBuilderLinkItem(item))
              .filter((item): item is MenuPageBuilderLinkItem => !!item)
          : undefined,
      }
    : undefined

  const products = isRecord(value.products)
    ? {
        columnsPerRow: sanitizeInt(value.products.columnsPerRow, 1, 6),
        columnsPerRowMobile: sanitizeInt(value.products.columnsPerRowMobile, 1, 3),
      }
    : undefined

  const pageBuilder: MenuPageBuilder = {
    enabled: sanitizeBoolean(value.enabled),
    useAsHomePage: sanitizeBoolean(value.useAsHomePage),
    layout: layout && hasDefinedValue(layout) ? layout : undefined,
    theme: theme && hasDefinedValue(theme) ? theme : undefined,
    search: search && hasDefinedValue(search) ? search : undefined,
    products: products && hasDefinedValue(products) ? products : undefined,
    footer: isRecord(value.footer)
      ? (() => {
          const columns = sanitizeFooterColumns(value.footer.columns)
          const footer = {
            enabled: sanitizeBoolean(value.footer.enabled),
            showTopBrands: sanitizeBoolean(value.footer.showTopBrands),
            showInfosLinks: sanitizeBoolean(value.footer.showInfosLinks),
            showFootnotes: sanitizeBoolean(value.footer.showFootnotes),
            showTimestamp: sanitizeBoolean(value.footer.showTimestamp),
            showDivider: sanitizeBoolean(value.footer.showDivider),
            dividerMarginTop: sanitizeInt(value.footer.dividerMarginTop, 0, 240),
            infosPaddingTop: sanitizeInt(value.footer.infosPaddingTop, 0, 240),
            infosPaddingBottom: sanitizeInt(value.footer.infosPaddingBottom, 0, 240),
            legalPaddingTop: sanitizeInt(value.footer.legalPaddingTop, 0, 240),
            legalPaddingBottom: sanitizeInt(value.footer.legalPaddingBottom, 0, 240),
            footnotesPaddingTop: sanitizeInt(value.footer.footnotesPaddingTop, 0, 240),
            footnotesPaddingBottom: sanitizeInt(value.footer.footnotesPaddingBottom, 0, 240),
            topBrandsTitle: sanitizeString(value.footer.topBrandsTitle),
            copyrightText: sanitizeString(value.footer.copyrightText),
            backgroundColor: sanitizeString(value.footer.backgroundColor),
            surfaceColor: sanitizeString(value.footer.surfaceColor),
            textColor: sanitizeString(value.footer.textColor),
            mutedTextColor: sanitizeString(value.footer.mutedTextColor),
            accentColor: sanitizeString(value.footer.accentColor),
            brandSectionBackgroundColor: sanitizeString(value.footer.brandSectionBackgroundColor),
            legalBackgroundColor: sanitizeString(value.footer.legalBackgroundColor),
            columnsPerRow: sanitizeInt(value.footer.columnsPerRow, 1, 6),
            columns: columns.length ? columns : undefined,
            topBrandsBlock: sanitizeBrandListBlock(
              isRecord(value.footer.topBrandsBlock) ? value.footer.topBrandsBlock : {}
            ),
            footnotesPrimaryHtml: sanitizeString(value.footer.footnotesPrimaryHtml),
            footnotesSecondaryHtml: sanitizeString(value.footer.footnotesSecondaryHtml),
          }

          return hasDefinedValue(footer) ? footer : undefined
        })()
      : undefined,
    hero,
    blocks: blocks?.length ? blocks : undefined,
  }

  if (!hasDefinedValue(pageBuilder as Record<string, unknown>)) {
    return undefined
  }

  return stripUndefinedDeep(pageBuilder)
}

export const hasMenuPageBuilderContent = (value: unknown): boolean => {
  const safeBuilder = sanitizeMenuPageBuilder(value)
  if (!safeBuilder) return false

  return Boolean(
    hasHeroContent(safeBuilder.hero)
    || safeBuilder.blocks?.length
  )
}

export const isMenuPageBuilderActive = (value: unknown): value is MenuPageBuilder => {
  const safeBuilder = sanitizeMenuPageBuilder(value)

  return safeBuilder?.enabled === true
}

export const menuPageBuilderSchema = z.object({
  enabled: z.boolean().optional(),
  useAsHomePage: z.boolean().optional(),
  layout: menuPageBuilderLayoutSchema.optional(),
  theme: menuPageBuilderThemeSchema.optional(),
  search: menuPageBuilderSearchSchema.optional(),
  products: menuPageBuilderProductsSettingsSchema.optional(),
  footer: menuPageBuilderFooterSchema.optional(),
  hero: menuPageBuilderHeroSchema.optional(),
  blocks: z.array(menuPageBuilderBlockSchema).optional(),
})
export type MenuPageBuilder = z.infer<typeof menuPageBuilderSchema>
